import { cn } from '@/utils/styles';
import { Card } from '@design-system/Card';
import { Switch } from '@design-system/Switch';
import { List, ListItem } from '@design-system/List';
import { useSaveFoodPreference } from '@viome/viome-base/src/usecase/Misc/FoodPreferenceMutation';
import { useGetInitData } from '@viome/viome-base/src/usecase/Bootup/BootupQuery';
import { useState, useEffect } from 'react';

export type SettingsPagePreferencesSectionProps = React.ComponentProps<'div'>;

export const SettingsPagePreferencesSection = ({
  className,
}: SettingsPagePreferencesSectionProps) => {

  const { data: initData, isLoading: isInitLoading } = useGetInitData();
  const [isVegetarian, setIsVegetarian] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: saveFoodPreference, isPending } = useSaveFoodPreference();

  useEffect(() => {
    if (initData?.isVegetarian !== undefined) {
      setIsVegetarian(initData.isVegetarian);
    }
  }, [initData?.isVegetarian]);

  const handleVegetarianToggle = (checked: boolean) => {
    setIsVegetarian(checked);
    setError(null);

    saveFoodPreference(
      { isVegetarian: checked },
      {
        onSuccess: (response: any) => {
          if (response.status === 'VM_ERROR') {
            setIsVegetarian(!checked);
            
            if (response.message.includes('Already the diet preference')) {
              setError('This preference is already set');
            } else {
              setError('Failed to update preference. Please try again.');
            }
          } else {
            setError(null);
          }
        },
        onError: () => {
          setIsVegetarian(!checked);
          setError('Failed to save preference. Please try again.');
        },
      }
    );
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <span className="typography-h2 text-primary mb-6">Preferences</span>

      <div className="flex flex-col gap-2">
        <Card>
          <List>
            <ListItem hideIndicator>
              I'm a vegetarian 🌱
              <Switch 
                className="ml-auto" 
                checked={isVegetarian}
                onCheckedChange={handleVegetarianToggle}
                disabled={isPending || isInitLoading}
              />
            </ListItem>
          </List>
        </Card>

        <span className="px-5 typography-caption1 text-tertiary">
          When selected, your Food Recommendations will only show vegetarian
          foods.
        </span>

        {error && (
          <span className="px-5 typography-caption1 text-red-500">
            {error}
          </span>
        )}

        {isPending && (
          <span className="px-5 typography-caption1 text-secondary">
            Updating preference...
          </span>
        )}
      </div>
    </div>
  );
};